import React, { useState, useEffect } from 'react';
import LotteryForm from './LotteryForm';
import './LotteryFormDesigner.css';

const LotteryFormDesigner = ({ formId, onSave, onPreview }) => {
  const [isDesignMode, setIsDesignMode] = useState(true);
  const [formConfig, setFormConfig] = useState({
    title: '幸运大抽奖',
    subtitle: '转动转盘，赢取精美奖品',
    prizes: [
      { name: '一等奖', probability: 5, color: '#FF6B6B', icon: '🎁', coupon: 'PRIZE_001' },
      { name: '二等奖', probability: 15, color: '#4ECDC4', icon: '🎁', coupon: 'PRIZE_002' },
      { name: '三等奖', probability: 30, color: '#45B7D1', icon: '🎁', coupon: 'PRIZE_003' },
      { name: '谢谢参与', probability: 50, color: '#96CEB4', icon: '🍀', coupon: null }
    ],
    theme: {
      primaryColor: '#FF6B6B',
      secondaryColor: '#FFE66D',
      backgroundColor: '#FFF5F5',
      textColor: '#333333'
    },
    settings: {
      allowMultipleDraws: false,
      showProbability: false,
      autoGenerateCoupon: true
    }
  });

  // 奖品图标选项
  const iconOptions = [
    '🎁', '🏆', '🥇', '🥈', '🥉', '🎊', '🎉', '🍀', '⭐', '💎',
    '🎯', '🎪', '🎨', '🎭', '🎪', '🎡', '🎠', '🎢', '🎫', '🎟️'
  ];

  // 颜色预设
  const colorPresets = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFE66D',
    '#FF8A80', '#80CBC4', '#90CAF9', '#A5D6A7', '#FFD54F',
    '#F8BBD9', '#B39DDB', '#81C784', '#FFB74D', '#F06292'
  ];

  // 更新表单配置
  const updateConfig = (key, value) => {
    setFormConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 更新奖品
  const updatePrize = (index, field, value) => {
    const newPrizes = [...formConfig.prizes];
    newPrizes[index] = { ...newPrizes[index], [field]: value };
    setFormConfig(prev => ({ ...prev, prizes: newPrizes }));
  };

  // 添加奖品
  const addPrize = () => {
    const newPrize = {
      name: '新奖品',
      probability: 10,
      color: colorPresets[Math.floor(Math.random() * colorPresets.length)],
      icon: iconOptions[Math.floor(Math.random() * iconOptions.length)],
      coupon: 'PRIZE_NEW'
    };
    setFormConfig(prev => ({
      ...prev,
      prizes: [...prev.prizes, newPrize]
    }));
  };

  // 删除奖品
  const removePrize = (index) => {
    if (formConfig.prizes.length > 1) {
      const newPrizes = formConfig.prizes.filter((_, i) => i !== index);
      setFormConfig(prev => ({ ...prev, prizes: newPrizes }));
    }
  };

  // 保存表单
  const handleSave = () => {
    if (onSave) {
      onSave(formConfig);
    }
  };

  // 预览表单
  const handlePreview = () => {
    setIsDesignMode(false);
    if (onPreview) {
      onPreview(formConfig);
    }
  };

  // 返回设计模式
  const backToDesign = () => {
    setIsDesignMode(true);
  };

  // 抽奖结果回调
  const handleLotteryResult = (result) => {
    console.log('抽奖结果:', result);
  };

  if (!isDesignMode) {
    return (
      <div className="lottery-preview">
        <div className="preview-header">
          <button className="back-btn" onClick={backToDesign}>
            ← 返回设计
          </button>
          <h3>预览模式</h3>
        </div>
        <LotteryForm 
          formData={formConfig} 
          onResult={handleLotteryResult}
          isPreview={true}
        />
      </div>
    );
  }

  return (
    <div className="lottery-designer">
      <div className="designer-header">
        <h2>抽奖表单设计器</h2>
        <div className="header-actions">
          <button className="preview-btn" onClick={handlePreview}>
            预览
          </button>
          <button className="save-btn" onClick={handleSave}>
            保存
          </button>
        </div>
      </div>

      <div className="designer-content">
        {/* 基本信息设置 */}
        <div className="config-section">
          <h3>基本信息</h3>
          <div className="form-group">
            <label>标题</label>
            <input
              type="text"
              value={formConfig.title}
              onChange={(e) => updateConfig('title', e.target.value)}
              placeholder="请输入抽奖标题"
            />
          </div>
          <div className="form-group">
            <label>副标题</label>
            <input
              type="text"
              value={formConfig.subtitle}
              onChange={(e) => updateConfig('subtitle', e.target.value)}
              placeholder="请输入副标题"
            />
          </div>
        </div>

        {/* 主题设置 */}
        <div className="config-section">
          <h3>主题设置</h3>
          <div className="theme-controls">
            <div className="form-group">
              <label>主色调</label>
              <div className="color-picker">
                <input
                  type="color"
                  value={formConfig.theme.primaryColor}
                  onChange={(e) => updateConfig('theme', {
                    ...formConfig.theme,
                    primaryColor: e.target.value
                  })}
                />
                <span>{formConfig.theme.primaryColor}</span>
              </div>
            </div>
            <div className="form-group">
              <label>辅助色</label>
              <div className="color-picker">
                <input
                  type="color"
                  value={formConfig.theme.secondaryColor}
                  onChange={(e) => updateConfig('theme', {
                    ...formConfig.theme,
                    secondaryColor: e.target.value
                  })}
                />
                <span>{formConfig.theme.secondaryColor}</span>
              </div>
            </div>
            <div className="form-group">
              <label>背景色</label>
              <div className="color-picker">
                <input
                  type="color"
                  value={formConfig.theme.backgroundColor}
                  onChange={(e) => updateConfig('theme', {
                    ...formConfig.theme,
                    backgroundColor: e.target.value
                  })}
                />
                <span>{formConfig.theme.backgroundColor}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 奖品设置 */}
        <div className="config-section">
          <h3>奖品设置</h3>
          <div className="prizes-config">
            {formConfig.prizes.map((prize, index) => (
              <div key={index} className="prize-config-item">
                <div className="prize-header">
                  <h4>奖品 {index + 1}</h4>
                  {formConfig.prizes.length > 1 && (
                    <button 
                      className="remove-btn"
                      onClick={() => removePrize(index)}
                    >
                      删除
                    </button>
                  )}
                </div>
                
                <div className="prize-fields">
                  <div className="form-group">
                    <label>奖品名称</label>
                    <input
                      type="text"
                      value={prize.name}
                      onChange={(e) => updatePrize(index, 'name', e.target.value)}
                      placeholder="请输入奖品名称"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>中奖概率 (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={prize.probability}
                      onChange={(e) => updatePrize(index, 'probability', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>奖品颜色</label>
                    <div className="color-picker">
                      <input
                        type="color"
                        value={prize.color}
                        onChange={(e) => updatePrize(index, 'color', e.target.value)}
                      />
                      <span>{prize.color}</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>奖品图标</label>
                    <div className="icon-picker">
                      <select
                        value={prize.icon}
                        onChange={(e) => updatePrize(index, 'icon', e.target.value)}
                      >
                        {iconOptions.map(icon => (
                          <option key={icon} value={icon}>{icon}</option>
                        ))}
                      </select>
                      <span className="selected-icon">{prize.icon}</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>券码前缀</label>
                    <input
                      type="text"
                      value={prize.coupon || ''}
                      onChange={(e) => updatePrize(index, 'coupon', e.target.value)}
                      placeholder="留空表示无奖品"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button className="add-prize-btn" onClick={addPrize}>
              + 添加奖品
            </button>
          </div>
        </div>

        {/* 功能设置 */}
        <div className="config-section">
          <h3>功能设置</h3>
          <div className="settings-controls">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formConfig.settings.allowMultipleDraws}
                  onChange={(e) => updateConfig('settings', {
                    ...formConfig.settings,
                    allowMultipleDraws: e.target.checked
                  })}
                />
                允许多次抽奖
              </label>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formConfig.settings.showProbability}
                  onChange={(e) => updateConfig('settings', {
                    ...formConfig.settings,
                    showProbability: e.target.checked
                  })}
                />
                显示中奖概率
              </label>
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formConfig.settings.autoGenerateCoupon}
                  onChange={(e) => updateConfig('settings', {
                    ...formConfig.settings,
                    autoGenerateCoupon: e.target.checked
                  })}
                />
                自动生成券码
              </label>
            </div>
          </div>
        </div>

        {/* 概率统计 */}
        <div className="config-section">
          <h3>概率统计</h3>
          <div className="probability-summary">
            <p>总概率: {formConfig.prizes.reduce((sum, prize) => sum + prize.probability, 0)}%</p>
            {formConfig.prizes.reduce((sum, prize) => sum + prize.probability, 0) !== 100 && (
              <p className="warning">⚠️ 总概率不等于100%，可能影响抽奖结果</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LotteryFormDesigner;
